"use client";

import { cn, Typography } from "@kyan/ui";
import { type FC } from "react";
import { FaArrowUp } from "react-icons/fa";

import { type LastTradesTableEntry } from "#/components/LastTradesTable/LastTradesTable.types";
import { formatCurrency } from "#/utils/valueFormatters";

import { LoadOrRender } from "../../LoadOrRender/LoadOrRender";
import type { OrderbookTableVariant } from "../OrderbookTable.types";

export interface OrderbookTickerProps {
  lastTrade?: LastTradesTableEntry;
  price?: number;
  indexPrice?: number;
  isLoading: boolean;
  variant: OrderbookTableVariant;
  isPerpPage?: boolean;
}

export const OrderbookTicker: FC<OrderbookTickerProps> = ({
  lastTrade,
  price,
  indexPrice,
  isLoading,
  variant,
  isPerpPage = true,
}) => {
  const lastTradePrice = lastTrade?.price;
  const isLastPriceBuy = lastTrade?.isBuy;

  if (variant === "options" && typeof price === "number") {
    return (
      <section className="text-textSecondary flex h-5.5 items-center justify-center gap-2 text-xs">
        <div className="flex w-32 justify-between">
          <Typography>Mark price:</Typography>
          <Typography>{formatCurrency(price)}</Typography>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn("flex h-8 justify-between gap-2 px-1 text-xs", {
        "pl-2": !isPerpPage,
      })}
    >
      <LoadOrRender className="h-7.5" isLoading={isLoading}>
        <div className="flex min-w-15 items-center gap-0.5">
          <Typography
            className={cn({
              "text-success": isLastPriceBuy,
              "text-error": !isLastPriceBuy,
            })}
          >
            {formatCurrency(lastTradePrice ?? 0)}
          </Typography>
          <FaArrowUp
            className={cn({
              "text-success": isLastPriceBuy,
              "text-error rotate-180": !isLastPriceBuy,
            })}
          />
        </div>
      </LoadOrRender>

      <div className="text-xxs flex min-w-10 flex-col items-center justify-center">
        <LoadOrRender className="h-2.5" isLoading={!price}>
          <Typography primary>{formatCurrency(price ?? 0)}</Typography>
        </LoadOrRender>
        <Typography variant="secondary">Mid</Typography>
      </div>

      <div className="text-xxs mr-1 flex min-w-10 flex-col items-end justify-center">
        <LoadOrRender className="h-2.5" isLoading={!indexPrice}>
          <Typography primary>{formatCurrency(indexPrice ?? 0)}</Typography>
        </LoadOrRender>
        <Typography variant="secondary">Index</Typography>
      </div>
    </section>
  );
};
